import React, { useEffect, useRef } from 'react';

import { useApiClient } from '@eluve/api-client-provider';
import {
  Button,
  DetailsSidebar,
  DetailsSidebarTrigger,
  Icon,
  Stack,
  Text,
  Textarea,
  useToast,
} from '@eluve/components';
import {
  useAppointmentId,
  useSummary,
} from '@eluve/frontend-appointment-hooks';
import { useNamedLogger } from '@eluve/logger';
import { useCaptureEvent } from '@eluve/posthog-react';
import { useTenantIdFromParams } from '@eluve/session-helpers';

export const customInstructionsExampleChips = [
  'Remove parts about...',
  'Include discussion about...',
  'Make notes more concise...',
  'Add more details in the plan section...',
];

type CustomInstructionsProps = {
  instructions: string;
  setInstructions: (instructions: string) => void;
  disabled?: boolean;
};

export const CustomInstructions: React.FC<CustomInstructionsProps> = ({
  instructions,
  setInstructions,
  disabled,
}) => {
  const toast = useToast();
  const appointmentId = useAppointmentId();
  const logger = useNamedLogger('CustomInstructions');
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const apiClient = useApiClient();
  const currentSummary = useSummary();
  const tenantId = useTenantIdFromParams();
  const didUserTypeInstruction = useRef<boolean>(false);

  const captureEvent = useCaptureEvent({
    tenantId,
    appointmentId,
  });
  useEffect(() => {
    textareaRef.current?.focus();
  }, []);

  const handleButtonClick = (instruction: string) => {
    captureEvent('user_clicked_regenerate_note_example_improvement_chip', {
      instruction,
    });

    const newInstructions = `${instructions ? `${instructions}\n` : ''}${instruction} `;
    setInstructions(newInstructions);
    textareaRef.current?.focus();
  };

  const handleRegenerate = async () => {
    logger.info('Regenerating summary with custom instructions', {
      appointmentId,
    });

    captureEvent('user_regenerated_note_with_custom_instructions', {
      instructions,
    });

    try {
      const result =
        await apiClient.llm.resummarizeAppointmentWithUserInstructions({
          body: {
            llmOutputId: currentSummary.llmOutputId!,
            userInstruction: instructions,
          },
          params: {
            tenantId: tenantId!,
            appointmentId,
          },
        });

      if (result.status !== 201) {
        throw new Error(
          'Failed to start summarization job with custom instructions',
        );
      }

      toast.success('Regenerating your summary');
    } catch (e) {
      toast.error('Failed to regenerate summary. Please try again');
    }

    return;
  };

  return (
    <>
      <DetailsSidebar
        size="lg"
        title="Improve Notes"
        description="Customize your notes by providing specific guidelines. Try to be
              as clear and concise as possible for best results."
      >
        <Stack className="p-8" align="stretch">
          <Textarea
            ref={textareaRef} // Attach the ref to the Textarea
            placeholder="Type your instructions here"
            className="min-h-[120px]"
            value={instructions}
            onChange={(e) => {
              if (!didUserTypeInstruction.current) {
                captureEvent('user_typed_improve_note_text');
                didUserTypeInstruction.current = true;
              }
              setInstructions(e.target.value);
            }}
          />
          <Stack className="py-8" gap={3} align="stretch">
            <Text color="tertiary">Examples</Text>
            {customInstructionsExampleChips.map((instruction, index) => (
              <div key={index}>
                <Button
                  variant="outline"
                  size="xs"
                  onClick={() => handleButtonClick(instruction)}
                >
                  {instruction}
                </Button>
              </div>
            ))}
          </Stack>
          <div className="pb-6">
            <Text>These changes will only apply to this note.</Text>
          </div>

          <Stack direction="horizontal" justify="end" align="stretch">
            <DetailsSidebarTrigger>
              <Button disabled={!instructions} onClick={handleRegenerate}>
                Regenerate Note
              </Button>
            </DetailsSidebarTrigger>
          </Stack>
        </Stack>
      </DetailsSidebar>
      <DetailsSidebarTrigger>
        <Button
          variant="outline"
          size="sm"
          disabled={disabled}
          onClick={() => {
            captureEvent('user_opened_improve_note_dialog');
          }}
        >
          <Icon name="WandSparkles" size="auto" />
          Improve Notes
        </Button>
      </DetailsSidebarTrigger>
    </>
  );
};
